/**
 * The caller's served standing on a virtual account (TMMAPI-0146 item 12a; MM-D-115 / 116): `myRole`,
 * `canManage`, `isOwner`, `ownerAccess` and `readOnlyUntil`, on every virtual account read — the list
 * row, the detail, `/me`'s summary and the shared-with-me row. Every role-aware control reads these;
 * nothing is inferred from the share list.
 */
import type { VirtualAccountSummary } from './generated';

export type VirtualAccountRole = 'OWNER' | 'MANAGER' | 'VIEWER';
export type OwnerAccess = 'ACTIVE' | 'GRACE' | 'LOCKED';

export interface MemberStanding {
  myRole: VirtualAccountRole;
  /** Manager-eligible writes are open: the owner or a Manager, on an ACTIVE virtual account, the owner ACTIVE. */
  canManage: boolean;
  isOwner: boolean;
  /** The owner's access, which a member's follows (MM-D-116). */
  ownerAccess: OwnerAccess;
  /** The owner's grace end, a civil date in the caller's timezone; only while `ownerAccess` is GRACE. */
  readOnlyUntil: string | null;
}

const ROLES: ReadonlySet<string> = new Set(['OWNER', 'MANAGER', 'VIEWER']);
const ACCESS: ReadonlySet<string> = new Set(['ACTIVE', 'GRACE', 'LOCKED']);
const CIVIL_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The standing fields every virtual account read serves (required by the contract since 0146). */
type WireStanding = Pick<
  VirtualAccountSummary,
  'myRole' | 'canManage' | 'isOwner' | 'ownerAccess' | 'readOnlyUntil'
>;

/**
 * The standing from a wire row, as served. An unknown role or access (a value added to the
 * contract later) reads as the fail-safe Viewer / ACTIVE.
 */
export function normalizeStanding(w: WireStanding): MemberStanding {
  const myRole: VirtualAccountRole = ROLES.has(w.myRole) ? w.myRole : 'VIEWER';
  const ownerAccess: OwnerAccess = ACCESS.has(w.ownerAccess) ? w.ownerAccess : 'ACTIVE';
  return {
    myRole,
    isOwner: w.isOwner,
    canManage: w.canManage,
    ownerAccess,
    readOnlyUntil:
      ownerAccess === 'GRACE' && w.readOnlyUntil !== undefined && CIVIL_DATE.test(w.readOnlyUntil)
        ? w.readOnlyUntil
        : null,
  };
}
